import { CONFIG } from "../../config";
import { unwrapEmailOnly } from "./components/MarkdownEditor/emailOnlyMarkdown";
import {
  ICreateReleaseNotePayload,
  IMediaUploadTarget,
  IReleaseNote,
  IReleaseNoteEmailPreview,
  IShareReleaseNotePayload,
  IShareReleaseNoteResult,
  ITranslateReleaseNotePayload,
  IUpdateReleaseNotePayload,
  ReleaseNoteAudience,
  ReleaseNoteLanguage,
  ReleaseNotePublishStatus,
} from "../../interfaces/releaseNote.interface";
import { getTokenizedAxios } from "../../utils/axiosWithTokenGen";

const BASE_PATH = `${CONFIG.API_SERVER.BASE_URL}/release-notes/v1/admin`;
const EMAIL_PREVIEW_PATH = `${CONFIG.API_SERVER.BASE_URL}/emails/v1/templates/feature-release-note/preview`;

export async function getReleaseNotesServer(params: {
  status?: ReleaseNotePublishStatus;
  search?: string;
}) {
  const axios = await getTokenizedAxios();
  return axios
    .get<IReleaseNote[]>(BASE_PATH, { params })
    .then((data) => data.data);
}

export async function getReleaseNoteServer(id: string) {
  const axios = await getTokenizedAxios();
  return axios
    .get<IReleaseNote>(`${BASE_PATH}/${id}`)
    .then((data) => data.data);
}

export async function createReleaseNoteServer(
  payload: ICreateReleaseNotePayload
) {
  const axios = await getTokenizedAxios();
  return axios.post<IReleaseNote>(BASE_PATH, payload).then((data) => data.data);
}

export async function updateReleaseNoteServer(params: {
  id: string;
  payload: IUpdateReleaseNotePayload;
}) {
  const axios = await getTokenizedAxios();
  return axios
    .put<IReleaseNote>(`${BASE_PATH}/${params.id}`, params.payload)
    .then((data) => data.data);
}

export async function deleteReleaseNoteServer(id: string) {
  const axios = await getTokenizedAxios();
  return axios.delete(`${BASE_PATH}/${id}`).then(() => ({ _id: id }));
}

export async function publishReleaseNoteServer(params: {
  id: string;
  audiences: ReleaseNoteAudience[];
}) {
  const axios = await getTokenizedAxios();
  return axios
    .post<IReleaseNote>(`${BASE_PATH}/${params.id}/publish`, {
      audiences: params.audiences,
    })
    .then((data) => data.data);
}

export async function unpublishReleaseNoteServer(params: {
  id: string;
  audiences: ReleaseNoteAudience[];
}) {
  const axios = await getTokenizedAxios();
  return axios
    .post<IReleaseNote>(`${BASE_PATH}/${params.id}/unpublish`, {
      audiences: params.audiences,
    })
    .then((data) => data.data);
}

export async function translateReleaseNoteServer(params: {
  id: string;
  payload: ITranslateReleaseNotePayload;
}) {
  const axios = await getTokenizedAxios();
  return axios
    .post<IReleaseNote>(`${BASE_PATH}/${params.id}/translate`, params.payload)
    .then((data) => data.data);
}

export async function shareReleaseNoteServer(params: {
  id: string;
  payload: IShareReleaseNotePayload;
}) {
  const axios = await getTokenizedAxios();
  return axios
    .post<IShareReleaseNoteResult>(
      `${BASE_PATH}/${params.id}/share`,
      params.payload
    )
    .then((data) => data.data);
}

export async function getMediaUploadUrlServer(contentType: string) {
  const axios = await getTokenizedAxios();
  return axios
    .post<IMediaUploadTarget>(`${BASE_PATH}/media/upload-url`, { contentType })
    .then((data) => data.data);
}

export async function uploadReleaseNoteImage(file: File): Promise<string> {
  const target = await getMediaUploadUrlServer(file.type);
  const response = await fetch(target.uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  if (!response.ok) {
    throw new Error(`Image upload failed with status ${response.status}`);
  }
  return target.publicUrl;
}

export async function previewReleaseNoteEmailServer(params: {
  locale: ReleaseNoteLanguage;
  title: string;
  body: string;
}) {
  const axios = await getTokenizedAxios();
  return axios
    .post<IReleaseNoteEmailPreview>(EMAIL_PREVIEW_PATH, {
      locale: params.locale,
      data: {},
      content: {
        subject: params.title,
        blocks: [{ type: "markdown", text: unwrapEmailOnly(params.body) }],
      },
    })
    .then((data) => data.data);
}
